/**
 * Generated table-alias suggestion for the unaliased post-relation slot.
 *
 * When the cursor sits immediately after an unaliased base-table reference in
 * FROM/JOIN (`FROM orders |`, `JOIN customers |`) the resolver additionally
 * emits ONE generated alias candidate (`o`, `oi`, `soh`, …). Shared pure
 * post-slot module wired once into each of the 5 per-engine resolvers, right
 * where they already branch on `classifyNextKeywordSlot` — no parallel
 * per-engine parsing (mirrors the `completionDocs.ts` precedent).
 *
 * Insert text is the bare alias, NO `AS` — legal on all 5 engines, and Oracle
 * forbids `AS` for table aliases.
 *
 * Ranking: the candidate carries `tier: 1, orderHint: 0`; the caller shifts
 * the legal-next-keyword `orderHint`s by one (`emitLegalNextKeywords(out,
 * slot, 1)`) so the alias is the FIRST ranked item at zero typed prefix —
 * above WHERE. With a typed prefix (`o`) it stays above keyword matches like
 * ORDER via exact-match label + the earlier sortText.
 *
 * Design note: an exact-typed alias (`o` typed, item label `o`) makes Enter a
 * no-op accept under `acceptSuggestionOnEnter: 'smart'`, so Enter falls
 * through to a newline — this is intentional and removes the `o`→ORDER hijack.
 */

import type { AliasBinding, CompletionCandidate } from '@/core/engines';

import type { NextKeywordSlotResult } from '../context/nextKeywordSlot';

const IDENT_CHAR = /[A-Za-z0-9_]/;
const ALIAS_SHAPE = /^[a-z][a-z0-9]*$/; // a usable alias: letter first, alphanumeric after

export type AliasSuggestionArgs = {
  readonly text: string;
  readonly offset: number;
  readonly statementStart: number;
  /** The SAME classification result the legal-next-keyword emission uses. */
  readonly slot: NextKeywordSlotResult;
  readonly aliasBindings: readonly AliasBinding[];
  /** The engine's keyword vocabulary (UPPER-cased members). */
  readonly keywords: ReadonlySet<string>;
};

/** Push the generated alias candidate when one applies. Returns true when a
 *  candidate was emitted so the caller can shift keyword orderHints by one. */
export function emitAliasCandidate(
  out: CompletionCandidate[],
  args: AliasSuggestionArgs,
): boolean {
  const candidate = buildAliasCandidate(args);
  if (!candidate) return false;
  out.push(candidate);
  return true;
}

export function buildAliasCandidate(args: AliasSuggestionArgs): CompletionCandidate | null {
  const { slot } = args;
  // Only the post-table slot of FROM/JOIN items, and only when no alias is
  // bound yet (`FROM orders o |` reports relationHasAlias — suppress).
  if (slot.slot !== 'after-from-relation-plain' && slot.slot !== 'after-join-target') return null;
  if (slot.relationHasAlias) return null;

  const rel = trailingRelationRef(args.text, args.offset, args.statementStart);
  if (!rel) return null; // `)` tail (derived table / table function) or unreadable — base tables only
  const tablePart = rel.segments[rel.segments.length - 1]!;
  // A bare trailing keyword (`FROM orders natural |`) is a continuation
  // keyword lexed as an identifier, not a relation — never alias it.
  if (!rel.lastQuoted && args.keywords.has(tablePart.toUpperCase())) return null;

  const taken = new Set<string>();
  for (const b of args.aliasBindings) {
    taken.add(b.alias.toLowerCase());
    taken.add(lastDottedSegment(b.relation).toLowerCase());
  }
  taken.add(tablePart.toLowerCase()); // the relation's own name is always taken

  const alias = generateTableAlias(tablePart, taken, args.keywords);
  if (!alias) return null;

  return {
    label: alias,
    insertText: alias,
    isSnippet: false,
    kind: 'alias', // maps to Monaco's Variable kind — visually distinct from keywords
    tier: 1,
    // NOT the `TYPE (qualifier)` convention — enrichCandidatesWithDocs only
    // touches column/table/view kinds, so this candidate passes through as-is.
    detail: 'alias',
    documentation: `Alias for \`${rel.segments.join('.')}\``,
    orderHint: 0,
    filterText: alias,
  };
}

/**
 * Generate an alias for `tableName` (already unquoted).
 *   - initials of words split on `_` / `-` / camelCase (`order_items` → `oi`,
 *     `SalesOrderHeader` → `soh`); single word → first letter (`orders` → `o`)
 *   - collision with an engine keyword or a taken name → first 3 letters of
 *     the first word (`ord`); still colliding → append 2, 3, …
 *   - nothing usable (empty, all-symbols, digit-leading) → null
 *
 * `taken` members must be lower-cased; `keywords` members UPPER-cased.
 */
export function generateTableAlias(
  tableName: string,
  taken: ReadonlySet<string>,
  keywords: ReadonlySet<string>,
): string | null {
  const words = splitIdentifierWords(tableName);
  if (words.length === 0) return null;
  const isFree = (a: string): boolean => !taken.has(a) && !keywords.has(a.toUpperCase());

  const initials = words
    .map((w) => w[0]!)
    .join('')
    .toLowerCase();
  if (ALIAS_SHAPE.test(initials) && isFree(initials)) return initials;

  const stem = words[0]!.slice(0, 3).toLowerCase();
  if (!ALIAS_SHAPE.test(stem)) return null;
  if (isFree(stem)) return stem;
  for (let n = 2; n < 100; n += 1) {
    const suffixed = `${stem}${n}`;
    if (isFree(suffixed)) return suffixed;
  }
  return null;
}

/** Split an identifier into words on `_`, `-`, whitespace, and camel/Pascal
 *  boundaries; drops non-alphanumeric residue and empty words. */
function splitIdentifierWords(name: string): readonly string[] {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // fooBar → foo Bar
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2') // HTTPServer → HTTP Server
    .split(/[^A-Za-z0-9]+/)
    .filter((w) => w.length > 0);
}

type RelationRef = {
  readonly segments: readonly string[]; // unquoted, as written (`main.orders` → ['main','orders'])
  readonly lastQuoted: boolean;
};

/** Read the dotted relation reference ending at the last non-whitespace char
 *  before the cursor. Returns null when the tail is not a plain (possibly
 *  quoted/dotted) identifier — e.g. a `)` from a derived table. */
function trailingRelationRef(
  text: string,
  offset: number,
  statementStart: number,
): RelationRef | null {
  let i = Math.min(offset, text.length);
  while (i > statementStart && /\s/.test(text[i - 1] ?? '')) i -= 1;
  if (i <= statementStart) return null;

  const segments: string[] = [];
  let lastQuoted = false;
  for (;;) {
    const seg = readSegmentBackward(text, i, statementStart);
    if (!seg) return null;
    segments.unshift(seg.name);
    if (segments.length === 1) lastQuoted = seg.quoted;
    i = seg.start;
    if (i > statementStart && text[i - 1] === '.') {
      i -= 1;
      continue;
    }
    break;
  }
  if (segments.some((s) => s.length === 0)) return null;
  return { segments, lastQuoted };
}

/** One identifier segment (bare or `"…"` / `` `…` `` / `[…]` delimited)
 *  ending at `end`; null when the tail char can't end a segment. */
function readSegmentBackward(
  text: string,
  end: number,
  statementStart: number,
): { readonly name: string; readonly start: number; readonly quoted: boolean } | null {
  const close = text[end - 1] ?? '';
  if (close === ']' || close === '"' || close === '`') {
    const open = close === ']' ? '[' : close;
    let j = end - 1;
    while (j > statementStart && text[j - 1] !== open) j -= 1;
    if (j <= statementStart) return null; // unterminated
    return { name: text.slice(j, end - 1), start: j - 1, quoted: true };
  }
  if (IDENT_CHAR.test(close)) {
    let j = end;
    while (j > statementStart && IDENT_CHAR.test(text[j - 1] ?? '')) j -= 1;
    return { name: text.slice(j, end), start: j, quoted: false };
  }
  return null;
}

function lastDottedSegment(name: string): string {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx + 1) : name;
}
